import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserPlus,
  TrendingUp,
  Calendar,
  CheckCircle,
  BookOpen,
  FileText,
  BarChart3,
  Timer,
  Clock
} from 'lucide-react';
import BarChart from '../../components/common/BarChart/BarChart';
import { API_BASE_URL } from '../../api/config';

const HRDashboard = () => {
  const navigate = useNavigate();
  const [timesheetEntries, setTimesheetEntries] = useState([]);
  const [isLoadingTimesheet, setIsLoadingTimesheet] = useState(false);

  // Mock data for HR dashboard
  const hrStats = [
    {
      title: 'Employees',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Recruitments',
      value: '23',
      change: '+5%',
      changeType: 'positive',
      icon: UserPlus,
      color: 'bg-green-500'
    },
    {
      title: 'Pending',
      value: '45',
      change: '-8%',
      changeType: 'negative',
      icon: Calendar,
      color: 'bg-yellow-500'
    },
    {
      title: 'Training',
      value: '12',
      change: '+3%',
      changeType: 'positive',
      icon: BookOpen,
      color: 'bg-purple-500'
    }
  ];

  // Function to fetch recent timesheet entries
  const loadRecentTimesheetEntries = async () => {
    try {
      setIsLoadingTimesheet(true);


      // Fetch last 7 days of timesheet entries
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);

      const queryParams = new URLSearchParams({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        limit: '5'
      });

      const url = `${API_BASE_URL}/timesheet-test?${queryParams.toString()}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();


        if (data.responseCode === 200 && data.data.entries) {
          setTimesheetEntries(data.data.entries);

        } else {

          setTimesheetEntries([]);
        }
      } else {

        setTimesheetEntries([]);
      }
    } catch (error) {

      setTimesheetEntries([]);
    } finally {
      setIsLoadingTimesheet(false);
    }
  };

  // Load timesheet entries on component mount
  useEffect(() => {
    loadRecentTimesheetEntries();
  }, []);

  // Convert timesheet entries to activity format
  const getTimesheetActivities = () => {
    return timesheetEntries.map((entry, index) => ({
      id: `timesheet-${entry.id || index}`,
      title: `Timesheet entry: ${entry.task}`,
      description: `${entry.employeeName || 'Employee'} - ${entry.projectName} (${entry.hours}h)`,
      time: new Date(entry.date).toLocaleDateString(),
      icon: Timer,
      color: 'text-[#0F2942]',
      entry: entry
    }));
  };

  // Static HR activities
  const staticActivities = [
    {
      id: 1,
      title: 'New employee onboarded',
      description: 'Sarah Johnson completed onboarding process',
      time: '2 hours ago',
      icon: UserPlus,
      color: 'text-green-600'
    },
    {
      id: 2,
      title: 'Leave request approved',
      description: 'John Smith\'s vacation request approved',
      time: '4 hours ago',
      icon: Calendar,
      color: 'text-[#0F2942]'
    },
    {
      id: 3,
      title: 'Performance review scheduled',
      description: 'Q4 reviews scheduled for 15 employees',
      time: '6 hours ago',
      icon: CheckCircle,
      color: 'text-purple-600'
    },
    {
      id: 4,
      title: 'Training completed',
      description: '10 employees completed safety training',
      time: '1 day ago',
      icon: BookOpen,
      color: 'text-yellow-600'
    }
  ];

  const recentActivities = [...getTimesheetActivities(), ...staticActivities].slice(0, 6);

  const quickActions = [
    {
      title: 'Add Employee',
      description: 'Onboard new team member',
      icon: UserPlus,
      color: 'bg-blue-500',
      action: () => navigate('/employee_database')
    },
    {
      title: 'Manage Leaves',
      description: 'Review leave requests',
      icon: Calendar,
      color: 'bg-green-500',
      action: () => navigate('/attendance_dashboard')
    },
    {
      title: 'Performance Reviews',
      description: 'Schedule performance reviews',
      icon: CheckCircle,
      color: 'bg-purple-500',
      action: () => navigate('/performance')
    },
    {
      title: 'Training Programs',
      description: 'Manage training sessions',
      icon: BookOpen,
      color: 'bg-yellow-500',
      action: () => navigate('/training_development')
    }
  ];

  const upcomingEvents = [
    {
      title: 'Team Meeting',
      date: 'Today',
      time: '2:00 PM',
      type: 'meeting'
    },
    {
      title: 'Performance Review',
      date: 'Tomorrow',
      time: '10:00 AM',
      type: 'review'
    },
    {
      title: 'Training Session',
      date: 'Friday',
      time: '3:00 PM',
      type: 'training'
    }
  ];

  // Employee Distribution Chart Data
  const employeeDistributionData = [
    { label: 'Full-time', value: 850 },
    { label: 'Part-time', value: 120 },
    { label: 'Contract', value: 180 },
    { label: 'Intern', value: 84 }
  ];

  // Leave Trends Chart Data
  const leaveTrendsData = [
    { label: 'Jan', value: 45 },
    { label: 'Feb', value: 38 },
    { label: 'Mar', value: 52 },
    { label: 'Apr', value: 41 },
    { label: 'May', value: 48 },
    { label: 'Jun', value: 35 },
    { label: 'Jul', value: 42 },
    { label: 'Aug', value: 39 },
    { label: 'Sep', value: 44 },
    { label: 'Oct', value: 37 },
    { label: 'Nov', value: 41 },
    { label: 'Dec', value: 33 }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-keka-page font-bold tracking-keka-heading text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-2">Manage workforce & metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {hrStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <div className="flex items-center mt-2">
                    <span className={`text-sm font-medium ${stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {stat.change}
                    </span>
                    <span className="text-sm text-gray-500 ml-1">from last month</span>
                  </div>
                </div>
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activities */}
        <div className="lg:col-span-2">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Activities</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={loadRecentTimesheetEntries}
                  disabled={isLoadingTimesheet}
                  className="text-sm text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50"
                >
                  {isLoadingTimesheet ? 'Loading...' : 'Refresh'}
                </button>
                <button className="text-sm text-agrosaf-green hover:text-agrosaf-darkGreen font-medium">
                  View All
                </button>
              </div>
            </div>
            <div className="space-y-4">
              {recentActivities.map((activity) => {
                const IconComponent = activity.icon;
                return (
                  <div key={activity.id} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`p-2 rounded-lg bg-gray-100`}>
                      <IconComponent className={`h-5 w-5 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{activity.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{activity.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Actions & Upcoming Events */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
            <div className="space-y-4">
              {quickActions.map((action, index) => {
                const IconComponent = action.icon;
                return (
                  <button
                    key={index}
                    onClick={action.action}
                    className="w-full p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${action.color}`}>
                        <IconComponent className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{action.title}</h3>
                        <p className="text-xs text-gray-600">{action.description}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Upcoming</h2>
            <div className="space-y-4">
              {upcomingEvents.map((event, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{event.title}</h3>
                    <p className="text-xs text-gray-600">{event.date} at {event.time}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${event.type === 'meeting' ? 'bg-blue-100 text-blue-800' :
                    event.type === 'review' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                    {event.type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Employee Distribution */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Distribution</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Users className="h-4 w-4 mr-1" />
              <span>1,234 Total</span>
            </div>
          </div>
          <div className="h-64">
            <BarChart
              data={employeeDistributionData}
              title="Employee Distribution"
              color="bg-blue-500"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-[#0F2942]">850</div>
              <div className="text-sm text-gray-600">Full-time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-500">120</div>
              <div className="text-sm text-gray-600">Part-time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">180</div>
              <div className="text-sm text-gray-600">Contract</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-300">84</div>
              <div className="text-sm text-gray-600">Intern</div>
            </div>
          </div>
        </div>

        {/* Leave Trends */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Leaves</h2>
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1" />
              <span>Monthly</span>
            </div>
          </div>
          <div className="h-64">
            <BarChart
              data={leaveTrendsData}
              title="Leave Trends"
              color="bg-orange-500"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="text-center p-2 bg-orange-50 rounded-lg">
              <div className="text-xl font-bold text-orange-600">45</div>
              <div className="text-xs text-gray-600">This Month</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xl font-bold text-gray-600">33</div>
              <div className="text-xs text-gray-600">Last Month</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRDashboard;
